import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { PubNubProvider } from "pubnub-react";
import Sidebar from "../../components/Sidebar";
import ChatInterface from "../../components/chat/ChatInterface";
import { getMentorsFromProfile } from "../../services/mentorMenteeChatService";
import { useConversationChat } from "../../hooks/useConversationChat";
import PubNub from "pubnub";


const MenteeChatContent = () => {
  const { id: userId, type: userType } = useSelector((s) => s.auth);
  const { data: mentors, isLoading: loadingMentors } = useQuery({
    queryKey: ["mentee_mentors", userId],
    queryFn: () => getMentorsFromProfile(userId),
    enabled: !!userId && userType === "user",
  });

  const [selectedMentor, setSelectedMentor] = useState(null);

  const { messages, isLoadingConversationMessages, isConnected, sendMessage } =
    useConversationChat(selectedMentor?._id, userId, userId);

  // Set first mentor as selected when mentors are loaded
  useEffect(() => {
    if (mentors && mentors.length > 0 && !selectedMentor) {
      setSelectedMentor(mentors[0]);
    }
  }, [mentors, selectedMentor]);

  const handleSendMessage = async (text) => {
    if (!text.trim() || !selectedMentor) return;
    await sendMessage(text);
  };

  return (
    <Sidebar>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
            <span className="text-white font-bold text-lg">💬</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Chat with Mentors</h1>
            <p className="text-sm text-gray-600">Connect with your mentors and get guidance</p>
          </div>
        </div>
        {isConnected && (
          <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            Connected
          </div>
        )}
      </div>

      {loadingMentors ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)] bg-white border border-gray-400 rounded-lg">
          <div className="text-center text-gray-500">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Loading chats...</h3>
            <p className="text-sm">Fetching mentors and their conversations</p>
          </div>
        </div>
      ) : mentors.length === 0 ? (
        <div className="flex items-center justify-center h-[calc(100vh-200px)] bg-white border border-gray-400 rounded-lg">
          <div className="text-center text-gray-500">
            <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
              <svg fill="currentColor" viewBox="0 0 24 24" className="w-full h-full">
                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H5V21H19V9Z"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold mb-2">No Mentors Yet</h3>
            <p className="text-sm mb-4">Mentors will appear here once you book appointments with them</p>
            <div className="text-xs text-gray-400">
              <p>• Browse available mentors in the Mentorship section</p>
              <p>• Book appointments to start your mentoring journey</p>
            </div>
          </div>
        </div>
      ) : (
        <ChatInterface
          items={mentors || []}
          selectedItem={selectedMentor}
          messages={messages}
          isLoadingMessages={isLoadingConversationMessages}
          title="My Mentors"
          leftColumnTitle="My Mentors"
          rightColumnTitle="Chat with Mentor"
          emptyStateTitle="No messages yet"
          emptyStateSubtitle="Send a message to start the conversation with your mentor"
          sidebarEmptyStateSubtitle="Connect with mentors to start chatting"
          inputPlaceholder="Share your thoughts with the mentor..."
          onItemSelect={setSelectedMentor}
          onSendMessage={handleSendMessage}
          selectedItemKey="_id"
        />
      )}
    </Sidebar>
  );
};

const MenteeChatPage = () => {
  const userId = useSelector((s) => s.auth.id);
  const pubnub = useMemo(
    () =>
      new PubNub({
        publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY || "demo",
        subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY || "demo",
        uuid: userId || "mentee",
      }),
    [userId]
  );

  return (
    <PubNubProvider client={pubnub}>
      <MenteeChatContent />
    </PubNubProvider>
  );
};

export default MenteeChatPage;
